// ── API client ──────────────────────────────────────────────────────────────
// Single place that knows about the token, the /api prefix and error shapes.

const TOKEN_KEY = "pd.token";
const USER_KEY  = "pd.username";
const PAGES_KEY = "pd.pages";

export const getToken    = () => localStorage.getItem(TOKEN_KEY);
export const getUsername = () => localStorage.getItem(USER_KEY);
export const getPages    = () => {
  const raw = localStorage.getItem(PAGES_KEY);
  if (!raw || raw === "null") return null;
  try { return JSON.parse(raw); } catch { return null; }
};

export const saveSession = (token, username, pages) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, username);
  localStorage.setItem(PAGES_KEY, JSON.stringify(pages ?? null));
};

export const clearSession = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(PAGES_KEY);
};

/** Set by App so an expired token bounces the user back to the login screen. */
let onUnauthorized = () => {};
export const setUnauthorizedHandler = (fn) => { onUnauthorized = fn; };

export class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

async function request(path, { method = "GET", body, signal } = {}) {
  const token = getToken();
  const res = await fetch(`/api${path}`, {
    method,
    signal,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  const text = await res.text();
  let data = null;
  if (text) {
    try { data = JSON.parse(text); } catch { data = text; }
  }

  if (!res.ok) {
    const code = (data && typeof data === "object" && data.code) || null;

    /*
     * A rejected token means the session is over - never silently show blanks.
     *
     * 401 and 403 are treated differently on purpose. 401 is "we do not know
     * who you are", which is always the end of the session, wherever it
     * happens. 403 is "we know who you are, you may not do this" - which is
     * the expected answer when a non-fd user touches an /admin endpoint, and
     * must not sign them out.
     *
     * The one 401 that is not a dead session is a failed step-up password
     * confirmation: that means "wrong password for this action". The server
     * tags those `reauth_failed`.
     */
    const sessionOver =
      path !== "/login"
      && code !== "reauth_failed"
      && (res.status === 401 || (res.status === 403 && !path.startsWith("/admin")));

    if (sessionOver) onUnauthorized();
    const msg =
      (data && typeof data === "object" && (data.message || data.error)) ||
      (typeof data === "string" && data) ||
      `Request failed (${res.status})`;
    throw new ApiError(msg, res.status);
  }
  return data;
}

export const api = {
  get:  (path, opts)       => request(path, { ...opts }),
  del:  (path, opts)       => request(path, { ...opts, method: "DELETE" }),
  put:  (path, body, opts) => request(path, { ...opts, method: "PUT", body }),
  post: (path, body, opts) => request(path, { ...opts, method: "POST", body }),
};

// ── endpoints ───────────────────────────────────────────────────────────────
export const login = (username, password) =>
  api.post("/login", { username, password });

export const fetchExpenses   = () => api.get("/exps");
/** The Expenses tab uses the username-scoped endpoint the original page used. */
export const fetchExpenseLedger = (username) =>
  api.get(`/expenses?username=${encodeURIComponent(username)}`);
export const fetchPeriods = (username) =>
  api.get(`/periods?username=${encodeURIComponent(username)}`);
export const fetchAccounts   = () => api.get("/accounts");
export const fetchCoExpenses = () => api.get("/co_exps");
export const fetchPmRevenue  = () => api.get("/co_exps_pm");
export const fetchReceivables = (username) =>
  api.get(`/ar?username=${encodeURIComponent(username)}`);
export const fetchUnbilled = (username) =>
  api.get(`/unbilled?username=${encodeURIComponent(username)}`);
export const fetchLabourBreakdown = () => api.get("/labour/breakdown");
export const fetchPrepaidReconciliation = () => api.get("/prepaid/reconciliation");

export const setExpenseFlag = (id, y_n) => api.put(`/expenses/${id}`, { y_n });
export const bulkSetExpenseAccount = (ids, account_number) =>
  api.put("/expenses/bulk-update", { ids, account_number });
export const saveUnbilledComment = (id, comments) =>
  api.put("/update-comments", { id, comments });

// ── admin endpoints ────────────────────────────────────────────────────────
export const migrateAdmin       = () => api.get("/admin/migrate");
export const listUsers          = () => api.get("/admin/users");
export const resetPassword      = (id, password) =>
  api.put(`/admin/users/${id}/password`, { password });
export const setPages           = (id, pages) =>
  api.put(`/admin/users/${id}/pages`, { pages });
export const createUser         = (username, password) =>
  api.post("/admin/users", { username, password });

// ── admin: spreadsheet imports ──────────────────────────────────────────────
export const listImports = () => api.get("/admin/imports");

/**
 * Upload a spreadsheet for an import. With commit=false nothing is written -
 * the server parses the file and returns a preview so the admin can check the
 * rows before replacing anything.
 */
export async function runImport(key, file, { commit = false, sheet, headerRow, append = false } = {}) {
  const body = new FormData();
  body.append("file", file);
  if (sheet) body.append("sheet", sheet);
  if (headerRow) body.append("headerRow", headerRow);

  const token = getToken();
  const res = await fetch(`/api/admin/imports/${key}?commit=${commit}&append=${append}`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body,
  });

  const text = await res.text();
  let data = null;
  if (text) { try { data = JSON.parse(text); } catch { data = text; } }

  if (!res.ok) {
    if (res.status === 401 || res.status === 403) onUnauthorized();
    throw new ApiError(
      (data && data.message) || (typeof data === "string" && data) || `Import failed (${res.status})`,
      res.status
    );
  }
  return data;
}

// ── Excel export ────────────────────────────────────────────────────────────
/**
 * Ship the rows currently on screen to the server and download the workbook.
 * Whatever the page has filtered down to is exactly what gets exported.
 */
export async function exportXlsx({ filename, sheet, title, columns, rows, totals }) {
  const token = getToken();
  const res = await fetch("/api/export/xlsx", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ filename, sheet, title, columns, rows, totals }),
  });

  if (!res.ok) {
    if (res.status === 401 || res.status === 403) onUnauthorized();
    let msg = `Export failed (${res.status})`;
    try { msg = (await res.json()).message || msg; } catch { /* keep default */ }
    throw new ApiError(msg, res.status);
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename || "export"}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ── fd: move a row to a different manager ───────────────────────────────────
/** Pass an empty username to clear the override and revert to the sheet. */
export const reallocate = (dataset, id, username, password) =>
  api.put("/reallocate", { dataset, id, username, password });

// ── admin: department -> manager mapping ────────────────────────────────────
export const listDepartments  = () => api.get("/admin/departments");
export const saveDepartment   = (code, username, label, effective_from, note) =>
  api.put("/admin/departments", { code, username, label, effective_from, note });
/** Pass a row id to remove one dated changeover, or a code to remove the lot. */
export const deleteDepartment = (codeOrId) =>
  api.del(`/admin/departments/${encodeURIComponent(codeOrId)}`);

// ── admin: locked periods ───────────────────────────────────────────────────
export const listPeriodLocks = () => api.get("/admin/period-locks");
export const lockPeriod   = (table_name, period, note) =>
  api.put("/admin/period-locks", { table_name, period, note });
export const unlockPeriod = (id) => api.del(`/admin/period-locks/${id}`);

// ── admin: account heads + chart of accounts ────────────────────────────────
export const listAccountHeads  = () => api.get("/admin/account-heads");
export const createAccountHead = (name) => api.post("/admin/account-heads", { name });
export const renameAccountHead = (id, name) => api.put(`/admin/account-heads/${id}`, { name });
export const removeAccountHead = (id) => api.del(`/admin/account-heads/${id}`);
export const listAllAccounts   = () => api.get("/admin/accounts");
export const setAccountHead    = (accountNumbers, head) =>
  api.put("/admin/accounts/head", { accountNumbers, head });

// ── salary map: person -> account head ──────────────────────────────────────
/** Readable by any user, so Expenses2 can collapse a person's rows. */
export const fetchSalaryMap = () => api.get("/salary-map");
export const listSalaryMap   = () => api.get("/admin/salary-map");
export const saveSalaryMap   = (head, people) =>
  api.put("/admin/salary-map", { head, people });
export const updateSalaryMap = (id, head, person) =>
  api.put(`/admin/salary-map/${id}`, { head, person });
export const removeSalaryMap = (id) => api.del(`/admin/salary-map/${id}`);

// ── employee -> manager, with effective dates ───────────────────────────────
export const listEmployeeManagers = () => api.get("/admin/employee-managers");
export const saveEmployeeManager  = (employee_code, manager, effective_from, note) =>
  api.put("/admin/employee-managers", { employee_code, manager, effective_from, note });
export const removeEmployeeManager = (id) => api.del(`/admin/employee-managers/${id}`);

// ── Deltek drop directory (automatic report pickup) ─────────────────────────
export const fetchDropStatus = () => api.get("/admin/drop-status");
export const runDropImport   = (key) => api.post(`/admin/imports/${key}/from-drop`, {});

/** 5-week rolling timesheet completeness, for chasing missing hours. */
export const fetchTimesheetStatus = (weeks = 5) =>
  api.get(`/labour/timesheet-status?weeks=${weeks}`);

/** Manager logins with readable names, for pages that show a manager. */
export const fetchManagers = () => api.get("/managers");
export const setManagerName = (username, display_name) =>
  api.put(`/admin/managers/${username}/name`, { display_name });

/**
 * Correct expense rows from the P&L. `action` is "account" (with
 * account_number), "zero" or "delete". Direct edits to the ledger — a later
 * import of the same period will undo them unless the period is locked.
 */
export const bulkEditExpenses = (ids, action, account_number) =>
  api.post("/expenses/bulk-edit", { ids, action, account_number });

/** Revenue and gross profit per partner per year, for the equity split. */
export const fetchEquityFlow = () => api.get("/equity/flow");
export const fetchEquityAudit = () => api.get("/equity/audit");
/* Saving a split. Pass runner_share null to clear the override and let the
   slice go back to following the rule. */
export const saveEquityProjectSplit = (body) =>
  api.post("/equity/project-split", body);
export const saveEquityPartnerRate = (username, runner_share) =>
  api.post("/equity/partner-rate", { username, runner_share });
export const fetchProjectSearch = (q, partner) =>
  api.get(`/projects/search?q=${encodeURIComponent(q || "")}`
        + (partner ? `&partner=${encodeURIComponent(partner)}` : ""));
export const fetchProject = (code, partner) =>
  api.get(`/project/${encodeURIComponent(code)}`
        + (partner ? `?partner=${encodeURIComponent(partner)}` : ""));
export const fetchEmployeeRates = () => api.get("/admin/employee-rates");
export const saveEmployeeRate = (code, body) =>
  api.put(`/admin/employee-rates/${encodeURIComponent(code)}`, body);

/* Manual accounting entries — adjustments fd types in, held outside the
   imported ledger so no import can overwrite them. */
export const listManualEntries  = () => api.get("/admin/manual-entries");
export const createManualEntry  = (e) => api.post("/admin/manual-entries", e);
export const updateManualEntry  = (id, e) => api.put(`/admin/manual-entries/${id}`, e);
export const removeManualEntry  = (id) => api.del(`/admin/manual-entries/${id}`);
